/**
 * Canje de invitación y restablecimiento de contraseña.
 *
 * Las dos pantallas son casi iguales y comparten el formulario a propósito: la
 * regla de contraseña —`passwordRule` en `@cleanblue/shared`— tiene que decir
 * lo mismo en los dos lados o la gente escribe una que después no le sirve.
 *
 * Comparten también la puerta de entrada: `TokenGate` decide si el enlace
 * todavía sirve **antes** de que se dibuje nada, y por eso encabezado y
 * formulario nunca pueden contradecirse.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { passwordRule } from '@cleanblue/shared';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/hooks';
import { homeFor, useSession } from '@/lib/session';
import { Field, Loading, Note, PasswordInput } from '@/components/ui';
import { AuthShell } from './LoginPage';

/**
 * La puerta: un enlace muerto no llega a mostrar formulario **ni encabezado**
 * de formulario.
 *
 * El canje en el servidor siempre fue de un solo uso, pero la pantalla no lo
 * consultaba: miraba si había algo en la query string y dibujaba. Un enlace ya
 * usado mostraba entonces «Creá tu contraseña» con un formulario que funcionaba
 * a la vista y solo fallaba al enviarlo — que se lee como «me está pidiendo la
 * contraseña otra vez» (reporte del dueño, 2026-09-07).
 *
 * Por eso la consulta vive acá arriba y no dentro del formulario: el título y
 * el subtítulo son parte de lo que hay que corregir, y desde adentro del
 * formulario no se alcanzan.
 */
function TokenGate({ kind, title, subtitle, children }: {
  kind: 'INVITE' | 'RESET';
  /** Encabezado de la pantalla **cuando el enlace sirve**. */
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { user, loading: sessionLoading } = useSession();
  const navigate = useNavigate();

  /* `retry: false` porque un token muerto responde 404 siempre: reintentar
     solo demora el mensaje (mismo criterio que `JoinUnitPage`).
     `refetchOnWindowFocus: false` porque el enlace se quema al canjearlo — sin
     esto, volver a la pestaña después de guardar la contraseña cambiaría el
     «Listo» por un «ya no sirve». */
  const status = useApi<{ ok: true }>(
    token ? `/auth/token-status?token=${encodeURIComponent(token)}&kind=${kind}` : null,
    { retry: false, refetchOnWindowFocus: false },
  );

  if (sessionLoading || (token && status.isLoading)) {
    return (
      <AuthShell title="Revisando el enlace…">
        <Loading label="Un momento" />
      </AuthShell>
    );
  }

  if (!token || status.error || !status.data) {
    /* Ya entró y volvió a abrir el enlace: llevarlo a su portal es más útil que
       avisarle que venció algo que ya no necesita. Es el caso del reporte. */
    if (user) return <Navigate to={homeFor(user.role)} replace />;

    /* Nada más que eso: qué pasó y por dónde seguir. Sin formulario, sin
       «¿ya tenés cuenta?» —el botón ya es esa respuesta— y sin repetir el
       mensaje en un `Note` debajo del título que ya lo dice. */
    return (
      <AuthShell
        title="Este enlace ya no sirve"
        subtitle="Ya se usó o venció. Si ya tenés contraseña, entrá con ella."
      >
        <button
          type="button"
          className="btn btn--primary btn--block btn--lg"
          onClick={() => navigate('/ingresar')}
        >
          Ir a ingresar
        </button>
      </AuthShell>
    );
  }

  return <AuthShell title={title} subtitle={subtitle}>{children}</AuthShell>;
}

/** Solo se monta con el enlace ya verificado: acá el token siempre sirve. */
function PasswordForm({ endpoint, submitLabel, onDone }: {
  endpoint: string;
  submitLabel: string;
  onDone: () => void;
}) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rule = passwordRule.safeParse(password);
  const tooShort = password.length > 0 && !rule.success;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!rule.success || mismatch) return;

    setBusy(true);
    setError(null);
    try {
      await api.post(endpoint, { token, password });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Algo salió mal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {error && <Note tone="danger">{error}</Note>}

      <Field
        label="Contraseña nueva"
        hint="Mínimo 8 caracteres."
        error={tooShort ? rule.error?.issues[0]?.message : undefined}
      >
        <PasswordInput
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          required
        />
      </Field>

      <Field label="Repetila" error={mismatch ? 'Las dos contraseñas no coinciden' : undefined}>
        <PasswordInput
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          required
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block btn--lg"
        disabled={busy || !rule.success || mismatch}
      >
        {busy ? 'Guardando…' : submitLabel}
      </button>
    </form>
  );
}

export function AcceptInvitePage() {
  const navigate = useNavigate();
  return (
    <TokenGate
      kind="INVITE"
      title="Creá tu contraseña"
      subtitle="Es la última vez que vas a necesitar este enlace"
    >
      <PasswordForm
        endpoint="/auth/accept-invite"
        submitLabel="Crear cuenta y entrar"
        onDone={() => { window.location.href = '/'; }}
      />
      <p className="muted small mt-3" style={{ textAlign: 'center' }}>
        ¿Ya tenés cuenta?{' '}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/ingresar')}>
          Ingresá
        </button>
      </p>
    </TokenGate>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  return (
    <TokenGate
      kind="RESET"
      title="Nueva contraseña"
      subtitle="El enlace vence a las dos horas de haberlo pedido"
    >
      {done ? (
        <>
          <Note tone="ok">Listo. Ya podés entrar con tu contraseña nueva.</Note>
          <button
            type="button"
            className="btn btn--primary btn--block mt-3"
            onClick={() => navigate('/ingresar')}
          >
            Ir a ingresar
          </button>
        </>
      ) : (
        <PasswordForm
          endpoint="/auth/reset-password"
          submitLabel="Guardar contraseña"
          onDone={() => setDone(true)}
        />
      )}
    </TokenGate>
  );
}
